import I18n from 'I18n';
import { useMemo } from 'react';
import { useFilterConfig } from '../filters/hooks/useFilterConfig';
/**
 * Hook that formats listing page titles for SEO according to the requirements:
 *
 * Breeze Agents: {Name} - AI {Team} Agent for HubSpot | {Provider}
 * Apps: {Name} - {Category} App For HubSpot | {Provider}
 */
export function useListingDocumentTitle(props) {
  const {
    AgentTeam,
    Category
  } = useFilterConfig();
  return useMemo(() => {
    if (!(props !== null && props !== void 0 && props.name) || !(props !== null && props !== void 0 && props.productType)) {
      return null;
    }

    // Handle categories - could be array or single string
    let normalizedCategories;
    if (props.categories && Array.isArray(props.categories)) {
      normalizedCategories = props.categories;
    } else if (props.category && typeof props.category === 'string') {
      normalizedCategories = [props.category];
    }
    const normalizedProvider = props.provider || props.providerName;
    const normalizedAgentTeam = props.agentTeam || props.team;
    if (props.productType === 'AGENT') {
      return formatAgentTitle(props.name, normalizedAgentTeam, normalizedProvider, AgentTeam);
    } else {
      return formatAppTitle(props.name, normalizedCategories, normalizedProvider, Category);
    }
  }, [props, AgentTeam, Category]);
}
function formatAgentTitle(name, agentTeam, provider, agentTeamFilter) {
  const teamName = getAgentTeamName(agentTeam, agentTeamFilter);
  const providerName = provider || '';
  if (teamName) {
    return I18n.text('marketplaceStorefront.listingTitles.agent.withTeam', {
      name,
      team: teamName,
      provider: providerName
    });
  } else {
    return I18n.text('marketplaceStorefront.listingTitles.agent.withoutTeam', {
      name,
      provider: providerName
    });
  }
}
function formatAppTitle(name, categories, provider, categoryFilter) {
  const categoryPart = formatCategoriesForTitle(categories, categoryFilter);
  return I18n.text('marketplaceStorefront.listingTitles.app.title', {
    name,
    categories: categoryPart || '',
    provider: provider !== null && provider !== void 0 ? provider : ''
  });
}
function getAgentTeamName(agentTeam, agentTeamFilter) {
  if (!agentTeam || !(agentTeamFilter !== null && agentTeamFilter !== void 0 && agentTeamFilter.options)) {
    return undefined;
  }

  // Find the matching option from the filter config
  const teamOption = agentTeamFilter.options.find(option => option.value === agentTeam);
  return teamOption === null || teamOption === void 0 ? void 0 : teamOption.text;
}
function formatCategoriesForTitle(categories, categoryFilter) {
  if (!categories || categories.length === 0) {
    return undefined;
  }

  // Translate category enum values using filter config
  const translatedCategories = categories.map(category => {
    var _categoryFilter$optio;
    // Find the matching option from the filter config
    const categoryOption = categoryFilter === null || categoryFilter === void 0 || (_categoryFilter$optio = categoryFilter.options) === null || _categoryFilter$optio === void 0 ? void 0 : _categoryFilter$optio.find(option => option.value === category);
    return categoryOption === null || categoryOption === void 0 ? void 0 : categoryOption.text; // Remove fallback to prevent enum values
  }).filter(text => Boolean(text)); // Type guard to ensure string[]

  if (translatedCategories.length === 0) {
    return undefined;
  }
  if (translatedCategories.length === 1) {
    return translatedCategories[0];
  }
  return I18n.formatList(translatedCategories);
}